import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "../lib/api-client";
import { queryKeys } from "../lib/queryClient";

/** No websocket infra in this app (see server.mjs/Railway notes) — chat is a plain
 *  short-interval poll, which is simple, works fine at this scale, and needs no sticky-session
 *  concerns behind Railway's proxy. */
const INBOX_POLL_MS = 20_000;
const THREAD_POLL_MS = 4_000;

export function useConversations() {
  return useQuery({
    queryKey: queryKeys.conversations,
    queryFn: ({ signal }) => apiClient.getConversations({}, signal),
    refetchInterval: INBOX_POLL_MS,
  });
}

export function useMessages(conversationId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.messages(conversationId ?? ""),
    queryFn: ({ signal }) => apiClient.getMessages(conversationId as string, signal),
    enabled: Boolean(conversationId),
    refetchInterval: THREAD_POLL_MS,
  });
}

/** Starts (or resumes) the buyer's thread with a listing's seller. */
export function useStartConversation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (listingId: string) => apiClient.startConversation(listingId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.conversations });
    },
  });
}

export function useSendMessage(conversationId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: string) => apiClient.sendMessage(conversationId as string, { body }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.messages(conversationId ?? "") });
      void queryClient.invalidateQueries({ queryKey: queryKeys.conversations });
    },
  });
}
